import React, { useState, useEffect } from 'react';
import { MagnifyingGlassIcon, PencilIcon, CubeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { AdminProduct } from '../../types/product';
import adminProductService from '../../services/adminProductService';
import ProductEditModal from './ProductEditModal';

/**
 * Страница справочника номенклатуры в админке
 */
const AdminProductsPage: React.FC = () => {
    const [products, setProducts] = useState<AdminProduct[]>([]);
    const [filteredProducts, setFilteredProducts] = useState<AdminProduct[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('all');
    const [statusFilter, setStatusFilter] = useState<string>('all');
    const [selectedProduct, setSelectedProduct] = useState<AdminProduct | null>(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);

    // Загрузка списка товаров
    const loadProducts = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await adminProductService.getAllProducts();
            setProducts(data);
            setFilteredProducts(data);
        } catch (err: any) {
            console.error('Ошибка при загрузке товаров:', err);
            setError(err.message || 'Не удалось загрузить товары');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadProducts();
    }, []);

    // Фильтрация товаров
    useEffect(() => {
        let filtered = [...products];

        // Поиск по названию, коду, артикулу
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            filtered = filtered.filter(
                (p) =>
                    p.name.toLowerCase().includes(query) ||
                    p.cod?.toLowerCase().includes(query) ||
                    p.article?.toLowerCase().includes(query) ||
                    p.guid.toLowerCase().includes(query)
            );
        }

        // Фильтр по категории
        if (categoryFilter !== 'all') {
            filtered = filtered.filter((p) => p.category === categoryFilter);
        }

        // Фильтр по статусу
        if (statusFilter === 'active') {
            filtered = filtered.filter((p) => p.active);
        } else if (statusFilter === 'inactive') {
            filtered = filtered.filter((p) => !p.active);
        }

        setFilteredProducts(filtered);
    }, [searchQuery, categoryFilter, statusFilter, products]);

    // Получение уникальных категорий
    const categories = Array.from(new Set(products.map((p) => p.category).filter(Boolean)));

    const handleEdit = (product: AdminProduct) => {
        setSelectedProduct(product);
        setIsEditModalOpen(true);
    };

    const handleModalClose = () => {
        setIsEditModalOpen(false);
        setSelectedProduct(null);
    };

    const handleProductUpdated = (updatedProduct: AdminProduct) => {
        setProducts((prev) =>
            prev.map((p) => (p.id === updatedProduct.id ? updatedProduct : p))
        );
        handleModalClose();
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="p-6">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
                    <p className="font-medium">Ошибка загрузки</p>
                    <p className="text-sm">{error}</p>
                    <button
                        onClick={loadProducts}
                        className="mt-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                    >
                        Повторить
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="p-6">
            <div className="mb-6">
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                    Справочник номенклатуры
                </h1>
                <p className="text-gray-600">
                    Управление товарами, загруженными из 1С
                </p>
            </div>

            {/* Фильтры и поиск */}
            <div className="bg-white rounded-lg shadow p-4 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    {/* Поиск */}
                    <div className="md:col-span-2">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                            <input
                                type="text"
                                placeholder="Поиск по названию, коду, артикулу..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            {searchQuery && (
                                <button
                                    onClick={() => setSearchQuery('')}
                                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                >
                                    <XMarkIcon className="h-4 w-4" />
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Фильтр по категории */}
                    <div>
                        <select
                            value={categoryFilter}
                            onChange={(e) => setCategoryFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">Все категории</option>
                            {categories.map((category) => (
                                <option key={category} value={category}>
                                    {category}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Фильтр по статусу */}
                    <div>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        >
                            <option value="all">Все товары</option>
                            <option value="active">Активные</option>
                            <option value="inactive">Неактивные</option>
                        </select>
                    </div>
                </div>

                {/* Статистика */}
                <div className="mt-4 flex items-center space-x-6 text-sm text-gray-600">
                    <div>
                        <span className="font-medium">Всего:</span> {products.length}
                    </div>
                    <div>
                        <span className="font-medium">Активных:</span>{' '}
                        {products.filter((p) => p.active).length}
                    </div>
                    <div>
                        <span className="font-medium">Найдено:</span> {filteredProducts.length}
                    </div>
                </div>
            </div>

            {/* Таблица товаров */}
            <div className="bg-white rounded-lg shadow overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Изображение
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Товар
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Категория
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Цена
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Статус
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Медиа
                                </th>
                                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Действия
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {filteredProducts.length === 0 ? (
                                <tr>
                                    <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                                        <CubeIcon className="mx-auto h-12 w-12 text-gray-400 mb-3" />
                                        <p>Товары не найдены</p>
                                    </td>
                                </tr>
                            ) : (
                                filteredProducts.map((product) => (
                                    <tr key={product.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {product.mainImageUrl ? (
                                                <img
                                                    src={product.mainImageUrl}
                                                    alt={product.name}
                                                    className="h-16 w-16 rounded-lg object-cover"
                                                />
                                            ) : (
                                                <div className="h-16 w-16 rounded-lg bg-gray-100 flex items-center justify-center">
                                                    <CubeIcon className="h-8 w-8 text-gray-400" />
                                                </div>
                                            )}
                                        </td>
                                        <td className="px-6 py-4">
                                            <div className="text-sm font-medium text-gray-900">
                                                {product.name}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {product.cod && `Код: ${product.cod}`}
                                                {product.article && ` • Арт: ${product.article}`}
                                            </div>
                                            <div className="text-xs text-gray-400">
                                                GUID: {product.guid}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {product.category || '—'}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {product.price ? (
                                                <span className="text-sm font-medium text-gray-900">
                                                    {product.price.toLocaleString('ru-RU')} ₸
                                                </span>
                                            ) : (
                                                <span className="text-sm text-gray-400">—</span>
                                            )}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span
                                                className={`px-2 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                                    product.active
                                                        ? 'bg-green-100 text-green-800'
                                                        : 'bg-gray-100 text-gray-800'
                                                }`}
                                            >
                                                {product.active ? 'Активен' : 'Неактивен'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            <div className="flex items-center space-x-2">
                                                {product.additionalImages &&
                                                    product.additionalImages.length > 0 && (
                                                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                                            +{product.additionalImages.length} фото
                                                        </span>
                                                    )}
                                                {product.certificateUrl && (
                                                    <span className="text-xs bg-purple-100 text-purple-800 px-2 py-1 rounded">
                                                        Сертификат
                                                    </span>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button
                                                onClick={() => handleEdit(product)}
                                                className="text-blue-600 hover:text-blue-900 inline-flex items-center"
                                            >
                                                <PencilIcon className="h-4 w-4 mr-1" />
                                                Редактировать
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Модальное окно редактирования */}
            {isEditModalOpen && selectedProduct && (
                <ProductEditModal
                    product={selectedProduct}
                    onClose={handleModalClose}
                    onUpdate={handleProductUpdated}
                />
            )}
        </div>
    );
};

export default AdminProductsPage;

